import Cookies from "universal-cookie";
import { Coordinate } from "../utils/types";
import { jwtDecode } from "jwt-decode";
import { ROLES } from "./constants";

const cookie = new Cookies();

const getAuthToken = () => cookie.get("access_token");

const setAuthToken = (token: string) => cookie.set("access_token", token);

const debounce = (callable: any, time: number) => {
	let timeout: ReturnType<typeof setTimeout>;

	return function (this: any) {
		const functionCall = () => callable.apply(this, arguments);

		clearTimeout(timeout);
		timeout = setTimeout(functionCall, time);
	};
};

// Función para calcular la distancia entre dos puntos (lat1, lng1) y (lat2, lng2) usando la fórmula de Haversine
function calcularDistancia(lat1: number, lng1: number, lat2: number, lng2: number) {
	const R = 6371000; // Radio de la Tierra en metros
	const rad = Math.PI / 180; // Para convertir grados a radianes

	const dLat = (lat2 - lat1) * rad;
	const dLng = (lng2 - lng1) * rad;

	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);

	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

	return R * c; // Distancia en metros
}

// Función principal para ajustar puntos

function separateClosePoints(cordenadas: Coordinate[], z: number = 1000): Coordinate[] {
	cordenadas.sort((a, b) => calcularDistancia(Number(a.lat), Number(a.lng), Number(b.lat), Number(b.lng)));
	const ret = cordenadas.map((cordenada: Coordinate, index: number) => {
		if (index === cordenadas.length - 1) return cordenada;
		// Verificar si los dos puntos son exactamente iguales
		if (cordenada.lat === cordenadas[index + 1].lat && cordenada.lng === cordenadas[index + 1].lng) {
			return {
				...cordenada,
				...transpolarCoordenada(
					Number(cordenada.lat),
					Number(cordenada.lng),
					Math.random() * z,
					Math.floor(Math.random() * 361)
				),
				color: cordenada.color,
			};
		}

		if (
			calcularDistancia(
				Number(cordenada.lat),
				Number(cordenada.lng),
				Number(cordenadas[index + 1].lat),
				Number(cordenadas[index + 1].lng)
			) < z
		) {
			const { lat, lng } = {
				...transpolarCoordenada(
					Number(cordenada.lat),
					Number(cordenada.lng),
					Math.random() * z,
					Math.floor(Math.random() * 361)
				),
			};
			return { ...cordenada, lat, lng, color: cordenada.color };
		} else {
			return cordenada;
		}
	});
	return ret;
}

function gradosARadianes(grados: number) {
	return grados * (Math.PI / 180);
}

// Función para convertir radianes a grados
function radianesAGrados(radianes: number) {
	return radianes * (180 / Math.PI);
}

// Función para transpolar un punto, a una distancia y dirección en grados dada
function transpolarCoordenada(lat1: number, lon1: number, distancia: number, gradosDireccion: number) {
	const R = 6371000; // Radio de la Tierra en metros

	// Convertir latitud, longitud y dirección de grados a radianes
	const lat1Rad = gradosARadianes(lat1);
	const lon1Rad = gradosARadianes(lon1);
	const direccionRad = gradosARadianes(gradosDireccion);

	// Calcular la nueva latitud
	const nuevaLatRad = Math.asin(
		Math.sin(lat1Rad) * Math.cos(distancia / R) +
			Math.cos(lat1Rad) * Math.sin(distancia / R) * Math.cos(direccionRad)
	);

	// Calcular la nueva longitud
	const nuevaLonRad =
		lon1Rad +
		Math.atan2(
			Math.sin(direccionRad) * Math.sin(distancia / R) * Math.cos(lat1Rad),
			Math.cos(distancia / R) - Math.sin(lat1Rad) * Math.sin(nuevaLatRad)
		);

	// Convertir las coordenadas de vuelta a grados
	const nuevaLat = radianesAGrados(nuevaLatRad);
	const nuevaLon = radianesAGrados(nuevaLonRad);

	return { lat: nuevaLat, lng: nuevaLon };
}

function isAdmin() {
	const token = localStorage.getItem("authToken");
	if (!token) return false;
	const user: any = jwtDecode(token);
	return user.role == ROLES.admin;
}

function isCollaborator() {
	const token = localStorage.getItem("authToken");
	if (!token) return false;
	const user: any = jwtDecode(token);
	return user.role == ROLES.admin;
}

function isAdminOrCollaborator() {
	const token = localStorage.getItem("authToken");
	if (!token) return false;
	const user: any = jwtDecode(token);
	return user.role == ROLES.admin || user.role == ROLES.collaborator;
}

function getCurrentUser() {
	const token = localStorage.getItem("authToken");
	if (!token) return;
	const user: any = jwtDecode(token);
	return user;
}

const saveFile = (data: Blob, filename: string) => {
	const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
	const link = document.createElement("a");
	link.href = url;
	link.setAttribute("download", filename);
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};

export default {
	isAdmin,
	isCollaborator,
	isAdminOrCollaborator,
	getCurrentUser,
	getAuthToken,
	setAuthToken,
	debounce,
	separateClosePoints,
	saveFile,
};
